import type { MutableRefObject } from 'react';

import type {
  DragEndProps,
  DragMoveProps,
  DragStartProps,
  PointerEventModifiers,
  RelativeAbsolutePosition,
} from '../../../../types/pointer-events.js';
import type { InteractionTriggers, ZoomBehavior } from '../../types.js';
import { centerPositionVertically } from '../../utils/center-position-vertically.js';
import { normalizeSelectionArea } from '../../utils/normalize-selection-area.js';

export const zoomDragStart = ({
  event,
  onSelectStart,
}: {
  event: DragStartProps & PointerEventModifiers;
  onSelectStart?: (event: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition;
  }) => void;
}) => {
  onSelectStart?.({
    triggeredBy: 'pointer',
    position: event.startHoldingAt,
  });
};

export const zoomDragMove = ({
  event,
  height,
  width,
  onSelectChange,
}: {
  event: DragMoveProps & PointerEventModifiers;
  height: number;
  width: number;
  onSelectChange?: (event: {
    triggeredBy: InteractionTriggers;
    start: RelativeAbsolutePosition;
    end: RelativeAbsolutePosition;
    mode: 'zoom' | 'explore';
  }) => void;
}) => {
  const {
    startHoldingAt: startHoldingAtProp,
    currentPosition: currentPositionProp,
  } = event;

  const startHoldingAt = centerPositionVertically(startHoldingAtProp, height);
  const currentPosition = centerPositionVertically(currentPositionProp, height);

  const [start, end] = normalizeSelectionArea([
    startHoldingAt,
    currentPosition,
  ]);

  if (start.relative.x < 0 || end.relative.x > width) {
    return;
  }

  onSelectChange?.({
    triggeredBy: 'pointer',
    start,
    end,
    mode: 'zoom',
  });
};
export const zoomDragEnd = ({
  event,
  height,
  width,
  lastMousePositionRef,
  onSelectEnd,
  onZoomChange,
}: {
  event: DragEndProps & PointerEventModifiers;
  height: number;
  width: number;
  lastMousePositionRef: MutableRefObject<RelativeAbsolutePosition | undefined>;
  onSelectEnd?: (
    event:
      | {
          triggeredBy: InteractionTriggers;
          start: RelativeAbsolutePosition;
          end: RelativeAbsolutePosition;
        }
      | { cancelled: true },
  ) => void;
  onZoomChange?: (event: {
    triggeredBy: InteractionTriggers;
    deltaStart: number;
    deltaEnd: number;
    behavior: ZoomBehavior;
  }) => void;
}) => {
  const { startHoldingAt: startHoldingAtProp, releasedAt: releasedAtProp } =
    event;

  const startHoldingAt = centerPositionVertically(startHoldingAtProp, height);
  const releasedAt = centerPositionVertically(releasedAtProp, height);

  const [start, end] = normalizeSelectionArea([startHoldingAt, releasedAt]);

  if (start.relative.x < 0 || end.relative.x > width) {
    return;
  }

  const deltaStart = start.relative.x;
  const deltaEnd = end.relative.x - width;

  onSelectEnd?.({ cancelled: true });
  onZoomChange?.({
    triggeredBy: 'pointer',
    deltaStart,
    deltaEnd,
    behavior: 'zoom-x',
  });
  lastMousePositionRef.current = releasedAt;
};
